<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMeterConsumptions',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = async () => {
    return props.meter.consumptions
      ? props.meter.consumptions.data
      : $fn.value.fetch('list_meter_consumptions', {
          parameters: { meter_id: props.meter.id },
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_8320016629450276_emit_lqs8s6saq_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_8320016629450276_close_mes3utorb_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_8320016629450276_showModal_uaq6xzgke_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterConsumption',
      props: {
        meter: props.meter,
      },
    };
  };
  const act_7989141091456081_emit_5ymk89mxi_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7989141091456081_close_bhu93msi4_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_7989141091456081_showModal_lif3fem2h_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterConsumption',
      props: {
        oid: $event.scope.item.id,
        meter: props.meter,
      },
    };
  };

  const act_8320016629450276_emit_lqs8s6saq = new actionTypes.EmitAction({
    actionArgs: act_8320016629450276_emit_lqs8s6saq_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_8320016629450276_close_mes3utorb =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_emit_lqs8s6saq,
      event: 'close',
      when: inst_evh_8320016629450276_close_mes3utorb_getWhen,
      displayName: 'emitReload',
    });
  const act_8320016629450276_showModal_uaq6xzgke =
    new actionTypes.ShowModalAction({
      actionArgs: act_8320016629450276_showModal_uaq6xzgke_getActionArgs,
      events: [inst_evh_8320016629450276_close_mes3utorb],
    });
  const inst_evh_8320016629450276_clickPreventStop_izsozxm5q =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_showModal_uaq6xzgke,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_izsozxm5q],
      event: 'click.prevent.stop',
    });
  const act_7989141091456081_emit_5ymk89mxi = new actionTypes.EmitAction({
    actionArgs: act_7989141091456081_emit_5ymk89mxi_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_7989141091456081_close_bhu93msi4 =
    new actionTypes.EventHandlerImpl({
      action: act_7989141091456081_emit_5ymk89mxi,
      event: 'close',
      when: inst_evh_7989141091456081_close_bhu93msi4_getWhen,
      displayName: 'emitReload',
    });
  const act_7989141091456081_showModal_lif3fem2h =
    new actionTypes.ShowModalAction({
      actionArgs: act_7989141091456081_showModal_lif3fem2h_getActionArgs,
      events: [inst_evh_7989141091456081_close_bhu93msi4],
    });
  const inst_evh_7989141091456081_clickPreventStop_6ftti1pfi =
    new actionTypes.EventHandlerImpl({
      action: act_7989141091456081_showModal_lif3fem2h,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7989141091456081_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7989141091456081_clickPreventStop_6ftti1pfi],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_8320016629450276_close_mes3utorb,
    emitReload: inst_evh_8320016629450276_close_mes3utorb,
    inst_evh_8320016629450276_clickPreventStop_izsozxm5q,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_7989141091456081_close_bhu93msi4,
    inst_evh_7989141091456081_clickPreventStop_6ftti1pfi,
    inst_evh_7989141091456081_clickPreventStop,
  };

  const evh_8320016629450276_close_mes3utorb = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_close_mes3utorb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_izsozxm5q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_izsozxm5q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7989141091456081_close_bhu93msi4 = async (event, scope) => {
    return await actions.inst_evh_7989141091456081_close_bhu93msi4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7989141091456081_clickPreventStop_6ftti1pfi = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7989141091456081_clickPreventStop_6ftti1pfi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7989141091456081_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7989141091456081_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpWidgetCard>
    <template #header>
      <ItpBox alignItems="center">
        <strong>
          {{ pgettext('MeterConsumptions', 'Meter Consumptions') }}
        </strong>
        <ItpButton
          :text="pgettext('MeterConsumptions', 'Add consumption...')"
          variant="light"
          size="sm"
          class="ms-auto"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </template>
    <BListGroup v-if="loaded">
      <BListGroupItem v-if="!items.length">
        <span>
          {{ pgettext('MeterConsumptions', 'No consumptions.') }}
        </span>
      </BListGroupItem>
      <BListGroupItem
        :key="item.id"
        class="d-flex flex-column"
        v-for="item in items"
      >
        <ItpBox alignItems="start">
          <ItpBox direction="column" class="w-100">
            <ItpBox class="W-100">
              <ItpBox direction="column" class="col-md-2">
                <strong>
                  {{ date(item.start.date) }}
                </strong>
                <small>
                  {{ number(item.start.reading) }} {{ item.unit }}
                </small>
              </ItpBox>
              <ItpBox direction="column" class="col-md-2">
                <strong>
                  {{ date(item.end.date) }}
                </strong>
                <small> {{ number(item.end.reading) }} {{ item.unit }} </small>
              </ItpBox>
              <ItpBox direction="column" class="col-md-2">
                <strong>
                  {{ number(item.total_consumption) }} {{ item.unit }}
                </strong>
              </ItpBox>
              <ItpBox alignItems="center" direction="column" class="col-md-2">
                <ItpBadge
                  :title="gettext('Billing factor')"
                  no-box-shadow
                  variant="light"
                  class="mx-3"
                  v-if="item.billing_factor"
                >
                  <ItpIcon name="fa-times"> </ItpIcon>
                  <ItpText :text="item.billing_factor"> </ItpText>
                </ItpBadge>
                <ItpBadge
                  variant="warning"
                  :text="pgettext('RentalService', 'Non billable')"
                  no-box-shadow
                  class="mx-3 mt-2"
                  v-if="item.non_billable"
                >
                </ItpBadge>
              </ItpBox>
              <ItpBox
                alignItems="start"
                wrap-items
                class="col-md-2"
                v-if="item.meter"
              >
                <UcCompactMeterDisplay :payload="item.meter" class="ps-3">
                </UcCompactMeterDisplay>
              </ItpBox>
              <ItpBox
                alignItems="start"
                wrap-items
                class="col-md-2"
                v-if="item.consumer"
              >
                <UcCompactCompanyDisplay :payload="item.consumer" class="ps-3">
                </UcCompactCompanyDisplay>
              </ItpBox>
            </ItpBox>
            <ItpBox>
              <div class="col-md-7">
                <ItpText :text="item.note" pre v-if="item.note"> </ItpText>
              </div>
            </ItpBox>
          </ItpBox>
          <ItpButton
            icon="fa-pen"
            :tooltip="gettext('Edit')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_7989141091456081_clickPreventStop($event, { item })
            "
          >
          </ItpButton>
        </ItpBox>
      </BListGroupItem>
    </BListGroup>
  </ItpWidgetCard>
</template>
